import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import CertificateRender from "@/components/CertificateRender";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CertificateViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const certRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/certificates/${id}`);
        setCert(data);
      } catch (e) {
        toast.error("Certificate not found");
        navigate("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const verifyUrl = `${window.location.origin}/verify/${id}`;

  const exportPdf = async () => {
    if (!certRef.current) return;
    setExporting(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");
      const canvas = await html2canvas(certRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#ffffff",
        imageTimeout: 15000,
      });
      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const ratio = canvas.width / canvas.height;
      let imgW = pageWidth;
      let imgH = imgW / ratio;
      if (imgH > pageHeight) {
        imgH = pageHeight;
        imgW = imgH * ratio;
      }
      const xOff = (pageWidth - imgW) / 2;
      pdf.addImage(imgData, "PNG", xOff, 0, imgW, imgH);
      pdf.save(`Endorsement_${cert.coc_number}_${cert.holder_name.replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error(err);
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center parchment">
        <Loader2 className="h-6 w-6 animate-spin text-[#0a1d3f]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen parchment pb-12" data-testid="cert-view-page">
      <header className="bg-[#0a1d3f] text-[#f4ecd5] border-b-4 border-[#d4af37] print:hidden">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="text-[#f4ecd5] hover:bg-[#d4af37]/10 hover:text-[#f4ecd5]"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Ledger
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              className="bg-transparent border-[#d4af37]/60 text-[#f4ecd5] hover:bg-[#d4af37]/10 hover:text-[#f4ecd5] rounded-none"
              data-testid="print-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button
              size="sm"
              disabled={exporting}
              onClick={exportPdf}
              className="bg-[#d4af37] hover:bg-[#b8860b] text-[#0a1d3f] rounded-none font-serif-display tracking-wider"
              data-testid="download-pdf-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? "Exporting…" : "Download PDF"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 mt-8">
        <div className="shadow-2xl">
          <CertificateRender ref={certRef} cert={cert} verifyUrl={verifyUrl} />
        </div>
      </main>
    </div>
  );
}
